import type { Location } from 'react-router-dom'

export function isSamePath(a: Location, b: Location): boolean {
  return a.pathname === b.pathname && a.search === b.search && a.hash === b.hash
}

export function isHashOnlyChange(from: Location, to: Location): boolean {
  return from.pathname === to.pathname && from.search === to.search && from.hash !== to.hash
}

export type NavigateDecision =
  | { type: 'queue' }
  | { type: 'noop' }
  | { type: 'instant'; target: Location }
  | { type: 'animate'; target: Location }

/**
 * Pure routing decision for Barba (display lags router during transitions).
 */
export function decideNavigation(
  target: Location,
  display: Location,
  isAnimating: boolean,
  shouldAnimate: (fromPath: string, toPath: string) => boolean,
): NavigateDecision {
  if (isAnimating) {
    return { type: 'queue' }
  }

  if (isSamePath(target, display)) {
    return { type: 'noop' }
  }

  if (isHashOnlyChange(display, target)) {
    return { type: 'instant', target }
  }

  if (!shouldAnimate(display.pathname, target.pathname)) {
    return { type: 'instant', target }
  }

  return { type: 'animate', target }
}

/**
 * Returns false if a transition is already in progress (caller must not start another).
 * Call synchronously before scheduling async transition work.
 */
export function tryAcquireTransitionLock(isAnimating: { current: boolean }): boolean {
  if (isAnimating.current) return false
  isAnimating.current = true
  return true
}

export function releaseTransitionLock(isAnimating: { current: boolean }): void {
  isAnimating.current = false
}
